import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type WikiPageDto } from '../lib/api.js';
import { useGm } from '../hooks/useGm.js';
import { StatCard, hasStatData } from '../components/wiki/StatCard.js';
import { BlockPreview } from '../components/wiki/BlockPreview.js';
import type { Block } from '../lib/entityBlocks.js';
import '../styles/editor.css';

export function WikiPage(): JSX.Element {
  const { slug = '' } = useParams<{ slug: string }>();
  const [page, setPage] = useState<WikiPageDto | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const { isGm } = useGm();
  const navigate = useNavigate();

  /**
   * Делегований клік на тілі сторінки: внутрішні wiki-посилання (<a href="/wiki/…">)
   * на простий лівий клік ідуть через SPA-навігацію (швидко). Ctrl/Cmd/Shift/Alt,
   * середня кнопка та правий клік лишаються нативними — «відкрити в новій вкладці» працює.
   */
  const handleBodyClick = useCallback((e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
    let el = e.target as HTMLElement | null;
    while (el && el.tagName !== 'A') el = el.parentElement;
    if (!el) return;
    const href = el.getAttribute('href') ?? '';
    if (href.startsWith('/wiki/')) {
      e.preventDefault();
      navigate(href);
    }
  }, [navigate]);

  useEffect(() => {
    setPage(null);
    setErr(null);
    if (!slug) return;
    api.wikiPage(slug).then(setPage).catch((e: Error) => setErr(e.message));
  }, [slug]);

  if (err) return <div className="wiki-error">⚠ {err}</div>;
  if (!page) return <div className="wiki-loading">завантаження…</div>;

  const meta: Array<{ label: string; cls?: string }> = [];
  if (page.category) meta.push({ label: page.category, cls: 'gold' });
  if (page.type && page.type !== page.category) meta.push({ label: page.type, cls: 'verd' });
  const fm = page.frontmatter as Record<string, unknown>;
  for (const k of ['tradition', 'tier', 'archetype', 'profession', 'race']) {
    const v = fm[k];
    if (typeof v === 'string' && v) meta.push({ label: `${k}: ${v}` });
  }

  const name = page.titleUk || page.titleEn || page.slug;
  const native = typeof fm.native === 'string' ? fm.native : undefined;
  const epithet = typeof fm.epithet === 'string' ? fm.epithet : undefined;
  const showCard = !!page.type && hasStatData(page.type, fm);
  const rawBlocks = Array.isArray(fm.blocks) ? (fm.blocks as Array<{ type: string; gm?: boolean; data?: Record<string, unknown> }>) : null;
  const blocks: Block[] | null = rawBlocks && rawBlocks.length
    ? rawBlocks.map((b, i) => ({ id: `b${i}`, type: b.type, gm: !!b.gm, collapsed: false, data: b.data ?? {} }))
    : null;

  return (
    <div className="dv-entity-wrap">
      {isGm && (
        <div className="gm-bar">
          <span className="badge">GM</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Тебе пускає GM_SECRET. Гравці цю смужку не бачать.</span>
          <span className="spacer" />
          <Link to={`/wiki/${encodeURIComponent(slug)}/${blocks ? 'build' : 'edit'}`}>✎ Редагувати</Link>
          <Link to="/wiki/new">+ Створити</Link>
        </div>
      )}
      <nav className="wiki-breadcrumb">
        <Link to="/wiki">Вікі</Link>
        <span className="sep">/</span>
        <span>{page.treePath === '_root' ? 'Головна' : page.treePath}</span>
      </nav>

      {blocks ? (
        <BlockPreview core={{ type: page.type, name, nameOriginal: native ?? page.titleEn ?? '', epithet: epithet ?? '', slug: page.slug }} blocks={blocks} gm={isGm} />
      ) : (
      <div className={`dv-entity ${showCard ? '' : 'dv-entity--solo'}`}>
        <article className="wiki-page dv-entity__prose">
          <div className="wiki-meta">
            {meta.map((m, i) => (
              <span key={`${m.label}-${i}`} className={`chip ${m.cls ?? ''}`}>{m.label}</span>
            ))}
          </div>

          <h1>{name}</h1>
          {page.titleEn && page.titleEn !== page.titleUk && <p className="subtitle">{page.titleEn}</p>}
          {epithet && <p className="subtitle" style={{ fontStyle: 'italic' }}>{epithet}</p>}

          <div className="ornament-rule">
            <div className="line" />
            <span className="glyph">✦</span>
            <div className="line" />
          </div>

          <div
            className="body"
            onClick={handleBodyClick}
            dangerouslySetInnerHTML={{ __html: page.bodyHtml }}
          />

          {page.backlinks.length > 0 && (
            <section className="wiki-backlinks">
              <h3>Згадки про цю сторінку</h3>
              <ul>
                {page.backlinks.map((b) => (
                  <li key={b.slug}>
                    <Link to={`/wiki/${encodeURIComponent(b.slug)}`}>{b.titleUk || b.slug}</Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </article>

        {showCard && (
          <aside className="dv-entity__rail">
            <StatCard type={page.type} name={name} epithet={epithet} native={native} fm={fm} isGm={isGm} />
          </aside>
        )}
      </div>
      )}
    </div>
  );
}
